import { CSSProperties, ReactNode } from 'react';

import { CFPBox, CFPButton, CFPInputFieldPassword } from '@bcp-channels-sharedcomponents-reactjs/cf-proxy';
import { ArrowRight } from '@bcp-nextgen-dx-component-factory/icons-accolade';
import { AppComposableTypography, InvalidDataAlert } from '@components';
import { UIHelper } from '@helpers';

import { FormHint } from '../formHint';
import { usePasswordFormHelper } from './passwordForm.helper';
import { resources } from './passwordForm.types';

export interface PasswordFormProps {
	logo?: ReactNode;
	styles?: CSSProperties;
}

export const PasswordForm = ({ logo, styles }: PasswordFormProps) => {
	const {
		passwordRef,
		confirmPasswordRef,
		isFormValid,
		passwordValidations,
		isPasswordValid,
		accountUserCredentialsErrorMessage,
		t,
		sanitizeAlphanumeric,
		handlePasswordChange,
		handleConfirmPasswordChange,
		getCFPInputProps,
		submitForm,
	} = usePasswordFormHelper();

	const { value, ...passwordInputProps } = getCFPInputProps('password');
	const { value: confirmValue, ...confirmPasswordInputProps } = getCFPInputProps('confirmPassword');

	return (
		<CFPBox stack styles={styles}>
			{logo && <CFPBox styles={{ marginBottom: UIHelper.rem(48) }}>{logo}</CFPBox>}

			<AppComposableTypography appearance="subtitle-200-01" text={t(resources.title)} />

			<CFPBox stack styles={{ marginTop: UIHelper.rem(24) }}>
				<CFPInputFieldPassword
					{...passwordInputProps}
					ref={passwordRef}
					name="password"
					autoFocus
					maxLength={8}
					label={t(resources.input1.label)}
					onChange={(v) => handlePasswordChange(v?.toString() || '')}
					onInput={() => sanitizeAlphanumeric(passwordRef)}
				/>
			</CFPBox>

			<CFPBox stack styles={{ marginTop: UIHelper.rem(16), gap: UIHelper.rem(8) }}>
				<FormHint
					label={t(resources.hint.mandatoryLowerCase)}
					isValid={!passwordValidations.required?.message && !passwordValidations.mandatoryLowerCase?.message}
				/>
				<FormHint
					label={t(resources.hint.mandatoryUpperCase)}
					isValid={!passwordValidations.required?.message && !passwordValidations.mandatoryUpperCase?.message}
				/>
				<FormHint
					label={t(resources.hint.mandatoryDigit)}
					isValid={!passwordValidations.required?.message && !passwordValidations.mandatoryDigit?.message}
				/>
				<FormHint
					label={t(resources.hint.requiredLength)}
					isValid={!passwordValidations.required?.message && !passwordValidations.requiredLength?.message}
				/>
			</CFPBox>

			<CFPBox stack styles={{ marginTop: UIHelper.rem(32) }}>
				<CFPInputFieldPassword
					{...confirmPasswordInputProps}
					ref={confirmPasswordRef}
					name="confirmPassword"
					maxLength={8}
					disabled={!isPasswordValid}
					label={t(resources.input2.label)}
					onChange={(v) => handleConfirmPasswordChange(v?.toString() || '')}
					onInput={() => sanitizeAlphanumeric(confirmPasswordRef)}
				/>
			</CFPBox>

			<CFPBox stack styles={{ alignSelf: 'start', marginTop: UIHelper.rem(40), gap: UIHelper.rem(40) }}>
				<InvalidDataAlert description={accountUserCredentialsErrorMessage || ''} />

				<CFPButton
					label={t(resources.button.continue)}
					size="large"
					endContent={ArrowRight.Outline}
					disabled={!isFormValid}
					onPress={submitForm}
				/>
			</CFPBox>
		</CFPBox>
	);
};
